/**
 * Breadcrumb Context
 *
 * Centralizes breadcrumb state in the layout so the breadcrumb component
 * stays mounted across route changes. This lets AnimatePresence animate
 * crumb additions/removals smoothly even when navigating between routes.
 *
 * Usage:
 *   In a page: useSetBreadcrumb([{ label: "Settings", to: "/settings" }, { label: "Profile" }])
 *   In layout: <BreadcrumbProvider> wraps the app; <BreadcrumbHost /> renders the persistent breadcrumb.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { BreadcrumbItem } from "../components/reports/ReportBreadcrumb";

interface BreadcrumbContextValue {
  items: BreadcrumbItem[];
  trailing: ReactNode | null;
  onBeforeNavigate: (() => void) | null;
  setBreadcrumb: (items: BreadcrumbItem[], opts?: { trailing?: ReactNode; onBeforeNavigate?: () => void }) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<BreadcrumbItem[]>([]);
  const [trailing, setTrailing] = useState<ReactNode | null>(null);
  const [onBeforeNavigate, setOnBeforeNavigate] = useState<(() => void) | null>(null);

  const setBreadcrumb = useCallback<BreadcrumbContextValue["setBreadcrumb"]>(
    (nextItems, opts) => {
      setItems(nextItems);
      setTrailing(opts?.trailing ?? null);
      // Wrap callback in a function so React's setState doesn't try to invoke it as updater.
      setOnBeforeNavigate(() => opts?.onBeforeNavigate ?? null);
    },
    []
  );

  const value = useMemo(
    () => ({ items, trailing, onBeforeNavigate, setBreadcrumb }),
    [items, trailing, onBeforeNavigate, setBreadcrumb]
  );

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

export function useBreadcrumbState(): BreadcrumbContextValue {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) {
    // Fallback no-op for tests or trees rendered without the provider.
    return {
      items: [],
      trailing: null,
      onBeforeNavigate: null,
      setBreadcrumb: () => {},
    };
  }
  return ctx;
}

/**
 * Hook for pages to publish their breadcrumb items.
 * Items + options are re-published whenever they change.
 */
export function useSetBreadcrumb(
  items: BreadcrumbItem[],
  opts?: { trailing?: ReactNode; onBeforeNavigate?: () => void }
) {
  const { setBreadcrumb } = useBreadcrumbState();

  // Stash latest items + opts in a ref so the publish effect can read them
  // without needing them in its dep array.
  const latest = useRef({ items, opts });
  latest.current = { items, opts };

  // Signature of the items currently being published. We key on label + to +
  // onClick-presence so callers don't need to memoize their items array.
  const itemsKey = JSON.stringify(
    items.map((i) => [i.label, i.to ?? null, i.onClick ? 1 : 0])
  );

  // Track the last signature this hook *actually* published. This survives
  // re-renders but resets when the host page unmounts — which is what we want,
  // because each fresh mount should publish its items even if the structural
  // signature happens to match what some previous page already pushed into the
  // context.
  const lastPublishedRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastPublishedRef.current === itemsKey) return;
    lastPublishedRef.current = itemsKey;
    const { items: curItems, opts: curOpts } = latest.current;
    setBreadcrumb(curItems, curOpts);
  }, [itemsKey, setBreadcrumb]);
}
